import { useEffect, useState } from "react";
import { Phone, MessageSquare } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { PACKAGES } from "@/components/PricingPackages";

const VEHICLES = ["Sedan / Coupe", "SUV / Crossover", "Truck", "Van / Minivan"];

export default function BookingModal({ open, initialPackage, onClose }) {
  const [pkg, setPkg] = useState(PACKAGES[1]);
  const [vehicle, setVehicle] = useState(VEHICLES[0]);

  useEffect(() => {
    if (open && initialPackage) setPkg(initialPackage);
  }, [open, initialPackage]);

  const smsBody = encodeURIComponent(
    `Hi P&H Detailing! I'd like to book the ${pkg.name} (${pkg.price}) for my ${vehicle}. Please send me your availability.`
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="booking-modal"
        className="bg-[#12161F] border-white/10 text-slate-100 sm:max-w-lg rounded-2xl"
      >
        <DialogHeader>
          <DialogTitle className="font-syne font-extrabold uppercase text-2xl text-white tracking-tight">
            Book Your <span className="text-amber-400">Detail</span>
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            Pick your package and vehicle, then call or text — we reply fast.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">
            Package
          </p>
          <div className="grid grid-cols-3 gap-2" data-testid="booking-package-options">
            {PACKAGES.map((p) => (
              <button
                key={p.id}
                onClick={() => setPkg(p)}
                data-testid={`booking-package-${p.id}`}
                className={`rounded-xl border px-3 py-3 text-left transition-[border-color,background-color] duration-300 ${
                  pkg.id === p.id
                    ? "border-amber-500 bg-amber-500/10"
                    : "border-white/10 hover:border-white/25"
                }`}
              >
                <span className="block font-syne font-bold text-xs uppercase text-white leading-tight">
                  {p.name}
                </span>
                <span className="block mt-1 font-mono text-[10px] text-amber-400">{p.price}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">
            Vehicle Type
          </p>
          <div className="grid grid-cols-2 gap-2" data-testid="booking-vehicle-options">
            {VEHICLES.map((v) => (
              <button
                key={v}
                onClick={() => setVehicle(v)}
                data-testid={`booking-vehicle-${v.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`rounded-xl border px-4 py-3 text-sm font-medium transition-[border-color,background-color] duration-300 ${
                  vehicle === v
                    ? "border-amber-500 bg-amber-500/10 text-white"
                    : "border-white/10 text-slate-300 hover:border-white/25"
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        <div
          data-testid="booking-summary"
          className="mt-6 rounded-xl border border-amber-500/30 bg-amber-500/5 px-5 py-4"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
            Your Booking
          </p>
          <p className="mt-1.5 font-syne font-bold text-lg text-white">
            {pkg.name} <span className="text-amber-400">{pkg.price}</span>
            <span className="text-slate-400 font-jakarta font-normal text-sm"> — {vehicle}</span>
          </p>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <a
            href="tel:9197373526"
            data-testid="booking-call-button"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm px-5 py-3.5 transition-colors duration-300"
          >
            <Phone size={16} strokeWidth={2.5} /> Call Now
          </a>
          <a
            href={`sms:9197373526?&body=${smsBody}`}
            data-testid="booking-text-button"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 hover:border-amber-400/60 hover:bg-white/5 text-white font-semibold text-sm px-5 py-3.5 transition-[border-color,background-color] duration-300"
          >
            <MessageSquare size={16} className="text-amber-400" /> Text Us
          </a>
        </div>
        <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
          919-737-3526 — Call or text, 7 days a week
        </p>
      </DialogContent>
    </Dialog>
  );
}
